/**
 * PreLienInstructionsModal — shared modal for CA 20-Day Pre Lien Notice DIY instructions.
 * canonical: Step5PreLien.tsx InstructionsModal — extracted for reuse in:
 *   - Wizard Step 5 (Step5PreLien.tsx)
 *   - Project Hub Lien tab (LienTab.tsx)
 *
 * CA Civ §8200: certified mail within 20 days of first furnishing.
 * Prop changes vs original inline component:
 *   - isOpen: boolean gate (caller no longer wraps in {flag && <Modal />})
 *   - ownerAddress replaces ownerName
 *   - generalContractorAddress replaces contractor
 *   - lenderAddress added (was hardcoded "not on file" message)
 */

import { brand } from '../config/brand'

interface Props {
  isOpen:                     boolean
  onClose:                    () => void
  deadline?:                  string | null
  ownerAddress?:              string | null
  lenderAddress?:             string | null
  generalContractorAddress?:  string | null
}

export default function PreLienInstructionsModal({
  isOpen,
  onClose,
  deadline,
  ownerAddress,
  lenderAddress,
  generalContractorAddress,
}: Props) {
  if (!isOpen) return null

  const parsedDeadline = deadline ? new Date(deadline) : null
  const deadlineDisplay = parsedDeadline && !Number.isNaN(parsedDeadline.getTime())
    ? parsedDeadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '20 days from your first day of furnishing labor or materials'

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="instr-title"
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(9,9,11,.45)',
        backdropFilter: 'blur(2px)',
        zIndex: 600,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--rs)',
          padding: 28,
          maxWidth: 560,
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,.2)',
          margin: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          id="instr-title"
          style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}
          data-testid="prelien-instr-title"
        >
          How to file your 20-Day Pre Lien Notice (DIY)
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.55, marginBottom: 20 }}>
          California Civ §8200 requires certified mail within 20 days of first furnishing.
          Follow these steps to preserve your lien rights.
        </div>

        {/* Steps */}
        {[
          {
            n: '1',
            title: 'Print the Pre Lien Notice',
            body: (
              <span>
                We don&apos;t yet have a built-in PDF generator for the pre-lien notice (coming soon —
                see B-22/B-23 on our roadmap). For now, draft the notice using a standard CA 20-Day
                Pre Lien Notice template that includes the project info, owner info, and your contractor
                info shown on this page.
              </span>
            ),
          },
          {
            n: '2',
            title: 'Sign the notice',
            body: (
              <span>
                Sign as the contractor. Include your CSLB license number under your signature.
              </span>
            ),
          },
          {
            n: '3',
            title: 'Mail via USPS Certified Mail with Return Receipt',
            body: (
              <span>
                Take the signed notice to a USPS location. Request{' '}
                <strong style={{ color: 'var(--ink)' }}>&quot;Certified Mail with Return Receipt&quot;</strong>{' '}
                service. Keep the green return-receipt card and the tracking number — these are your
                proof of compliance if you ever need to enforce a lien.
                <br /><br />
                Mail one copy to each recipient:
                <ul style={{ margin: '8px 0 8px 16px', padding: 0, fontSize: 12, color: 'var(--ink2)' }}>
                  <li style={{ marginBottom: 4 }}>
                    <strong style={{ color: 'var(--ink)' }}>Property owner:</strong>{' '}
                    {ownerAddress
                      ? <>{ownerAddress}</>
                      : <span style={{ color: 'var(--amber)' }}>Owner name and address not on file. Add in Project Hub → Settings.</span>
                    }
                  </li>
                  <li style={{ marginBottom: 4 }}>
                    <strong style={{ color: 'var(--ink)' }}>Construction lender (if applicable):</strong>{' '}
                    {lenderAddress
                      ? <>{lenderAddress}</>
                      : <span style={{ color: 'var(--amber)' }}>Lender not on file. Add in Project Hub → Settings if one exists.</span>
                    }
                  </li>
                  <li>
                    <strong style={{ color: 'var(--ink)' }}>Original contractor (if you are a subcontractor):</strong>{' '}
                    {generalContractorAddress
                      ? <>{generalContractorAddress}</>
                      : <span style={{ color: 'var(--ink3)' }}>Not applicable (you are the GC) or not yet set.</span>
                    }
                  </li>
                </ul>
                <strong style={{ color: 'var(--ink)' }}>Deadline:</strong>{' '}
                <span style={{ color: 'var(--red)', fontWeight: 600 }}>{deadlineDisplay}</span>
              </span>
            ),
          },
          {
            n: '4',
            title: 'Save your receipts',
            body: (
              <span>
                Once mailed, return to {brand.name} and we&apos;ll track the deadline. Save the
                certified-mail receipts in your project files — you&apos;ll need them if a payment
                dispute arises.
              </span>
            ),
          },
        ].map(step => (
          <div key={step.n} style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{
              flexShrink: 0,
              width: 24, height: 24,
              borderRadius: '50%',
              background: 'var(--ink)',
              color: 'var(--surface)',
              fontSize: 11, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {step.n}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>
                {step.title}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.6 }}>
                {step.body}
              </div>
            </div>
          </div>
        ))}

        <button
          className="btn b-dk btn-sm"
          onClick={onClose}
          data-testid="instr-close"
          style={{ marginTop: 4 }}
        >
          Got it
        </button>
      </div>
    </div>
  )
}
